// ----------------------------------------------------------------------

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '';

// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  register: path(ROOTS_AUTH, '/register'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  verify: path(ROOTS_AUTH, '/verify'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
};

export const PATH_DASHBOARD = {
  root: '/dashboard',
  general: {
    app: path(ROOTS_DASHBOARD, '/dashboard'),
  },
  permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    account: path(ROOTS_DASHBOARD, '/account'),
    plans: path(ROOTS_DASHBOARD, '/account/plans'),
    confirmation: path(ROOTS_DASHBOARD, '/account/plans/confirmation'),
    cancelConfirmation: path(ROOTS_DASHBOARD, '/account/plans/confirmation/cancel'),
  },
  credits: {
    root: path('', '/credits'),
    purchase: path('', '/credits/purchase'),
    // viewCredits: path('', `/credits/view-credits/business-transactions`), //?ownershipType=merchant
    purchaseHistory: path('',  `/credits/purchase-history`),
    viewTransaction: (id: string) => path('', `/credits/transaction/${id}`),

    browseProjects: path('', '/credits/projects'),
    portfolios: path('', '/credits/portfolios'),
    viewBasket: (basketId: string) => path('', `/credits/portfolio/${basketId}`),
    viewProject: (projectCode: string) => path('', `/credits/project/${projectCode}`),
    
    viewReceipt: (id: string) => path('', `/credits/transactions/${id}/receipt`),
    viewCertificate: (id: string) => path('', `/credits/transactions/${id}/certificate`),
    viewDetailedCertificate: (id: string) => path('', `/credits/transactions/${id}/detailed-certificate`),
    purchaseCredits: path('', '/credits/purchase-credits'),
  },
  programs: {
    root: path('', '/programs'),
    contributions: (type: string, code: string) => path('', `/programs/program/${type}/${code}/contributions`),
    emissions: (type: string, code: string) => path('', `/programs/program/${type}/${code}/emissions`),
    emissionRecord: (type: string, code: string, emissionRecordId: string) => path('', `/programs/program/${type}/${code}/emissions/${emissionRecordId}`),
    calculate: (type: string, code: string) => path('', `/programs/program/${type}/${code}/emissions/calculate`),
    addEmission: (type: string, code: string, emissionRecordId: string) => path('', `/programs/program/${type}/${code}/emissions/calculate/${emissionRecordId}`),
    program: (type: string, code: string) => path('', `/programs/program/${type}/${code}`),
    transactions: (type: string, code: string, queryParameters?: URLSearchParams) => path('', `/programs/program/${type}/${code}/transactions${queryParameters ? `?${queryParameters.toString()}` : ''}`),
    employees: (type: string, code: string) => path('', `/programs/program/${type}/${code}/employees`),
    customers: (type: string, code: string) => path('', `/programs/program/${type}/${code}/customers`),
    flights: (type: string, code: string) => path('', `/programs/program/${type}/${code}/flights`),
    impact: (type: string, code: string) => path('', `/programs/program/${type}/${code}/impact`),
    eventImpact: path('', `/programs/program/event/impact`),
    engagement: (type: string, code: string) => path('', `/programs/program/${type}/${code}/engagement`),
    configuration: (type: string, code: string) => path('', `/programs/program/${type}/${code}/configuration`),
    report: (type: string, code: string, dateFrom: string, dateTo: string) => path('', `/programs/program/${type}/${code}/${dateFrom}/${dateTo}/report`),
    collectiveCredits: path('', `/programs/collective-credits`),

    toolkit: path('', '/programs/toolkit'),
    flightCalculatorSettingsPage: path('', '/programs/toolkit/flight-calculator'),

    customer: {
      root: path('', '/programs/program/customer'),
      contributions: path('', '/programs/program/customer/contributions'),
    },

    event: {
      root: path('', '/programs/program/event'),
      overview: path('', '/programs/program/event/overview'),
      emissions: path('', '/programs/program/event/emissions'),
      amount: path('', '/programs/program/event/emissions/event-amount'),
      calculate: path('', '/programs/program/event/emissions/event-calculator'),
      edit: (footprintId: string) => path('', `/programs/program/event/emissions/event-calculator/${footprintId}/edit`),
      view: (footprintId: string) => path('', `/programs/program/event/emissions/event-calculator/${footprintId}`),
      amountEdit: (footprintId: string) => path('', `/programs/program/event/emissions/event-amount/${footprintId}/edit`),
      amountView: (footprintId: string) => path('', `/programs/program/event/emissions/event-amount/${footprintId}`),
    },
    business: {
      root: path('', '/programs/program/business'),
      contributions: path('', '/programs/program/business/contributions'),
      transaction: (transactionId: string) => path('', `/programs/program/business/contributions/transaction/${transactionId}`),
      emissions: path('', '/programs/program/business/emissions'),
      amount: path('', '/programs/program/business/emissions/business-amount'),
      calculate: path('', '/programs/program/business/emissions/business-calculator'),
      edit: (footprintId: string) => path('', `/programs/program/business/emissions/business-calculator/${footprintId}/edit`),
      view: (footprintId: string) => path('', `/programs/program/business/emissions/business-calculator/${footprintId}`),
      amountEdit: (footprintId: string) => path('', `/programs/program/business/emissions/business-amount/${footprintId}/edit`),
      amountView: (footprintId: string) => path('', `/programs/program/business/emissions/business-amount/${footprintId}`),
    },
    viewToolkitItem: (code: string) => path('', `/programs/toolkit/${code}`),
  },
  impactStories: {
    root: path('', '/impact-stories'),
    tools: path('', '/impact-stories/tools'),
    viewToolkitItem: (code: string) => path('', `/impact-stories/tools/${code}`),
    impactWidget: path('', '/impact-stories/tools/impact-widget'),

    impactPages: path('', '/impact-stories/pages'),
    createImpactPages: (type: string) => path('', `/impact-stories/pages/create?type=${type}`),
    updateImpactPages: (type: string, id: string) => path('', `/impact-stories/pages/update?type=${type}&id=${id}`),

    insights: {
      root: path('', '/insights'),
      projects: path('', '/insights/projects'),
      impact: path('', '/insights/impact'),
      transactions: path('', '/insights/transactions'),
      merchantReport: (dateFrom: string, dateTo: string) => path('', `/insights/${dateFrom}/${dateTo}/report`),
    }
  },
  settings: {
    root: path('', '/settings'),
    theme: path('', '/settings/theme'),
    business: path('', '/settings/business'),
    defaultBasket: path('', '/settings/default-basket'),
    billing: path('', '/settings/billing'),
    menu: path('', '/settings/menu'),
    emails: path('', '/settings/emails'),
    dashboard: path('', '/settings/dashboard'),
  },
  account: {
    root: path('', '/account'),
    plans: path('', '/account/plans'),
    accountDetails: path('', '/account/account-details'),
    userManagement: path('', '/account/user-management')
  },
};
